import { getCookie, setCookie } from './cookieUtils';
import configDetails from '../Config/Config';
import logger from './Logger';
// Token check cache to reduce console spam
let lastTokenCheck = { time: 0, result: null, expiresAt: null };

// Configuration response cookie helpers
export function storeConfigurationResponse(configResponse) {
  // Extract model_details from nested structure
  const modelDetails = configResponse.model_details || {};
  const { token, issued_at, expires_at, status, model } = modelDetails;
  
  // Always update timestamps regardless of status
  // This ensures our local expiry check stays in sync with server
  setCookie('token_issued_at', issued_at);
  setCookie('token_expires_at', expires_at);
  setCookie('token_status', status);

  // Store the transcription model type (assemblyai or deepgram)
  if (model) {
    setCookie('transcription_model', model);
  }
  
  if (status === 'new' || status === 'existing') {
    setCookie('transcription_token', token);
  }
  else {
    logger.error('Received unknown token status from server', {
      component: 'Record',
      action: 'TOKEN_STATUS_UNKNOWN',
      context: { status, model, response: configResponse }
    });
    console.warn('⚠️ Unknown token status:', status);
  }
}

// Get stored transcription model type
export function getStoredModel() {
  return getCookie('transcription_model') || configDetails.TRANSCRIPTION_MODEL_ASSEMBLYAI; // Default to assemblyai
}



export function getStoredToken() {
  const token = getCookie('transcription_token');
  const expiresAt = getCookie('token_expires_at');
  
  if (!token || !expiresAt) {
    return null;
  }
  
  // Use cache if we checked recently (within 1 second) and expiry time hasn't changed
  const now = Date.now();
  if (now - lastTokenCheck.time < 1000 && lastTokenCheck.expiresAt === expiresAt) {
    return lastTokenCheck.result;
  }
  
  try {
    let expiresAtUTC = expiresAt;
    if (!expiresAt.endsWith('Z') && !expiresAt.includes('+') && !expiresAt.includes('-', 10)) {
      expiresAtUTC = expiresAt + 'Z';
    }
    
    // Both times must be in UTC milliseconds for accurate comparison
    const currentUTCTimestamp = Date.now(); // This is always UTC milliseconds
    const expirationDate = new Date(expiresAtUTC);
    const expirationUTCTimestamp = expirationDate.getTime();
    
    // Check if the date is valid
    if (isNaN(expirationUTCTimestamp)) {
      logger.error('Invalid token expiration date format', {
        component: 'Record',
        action: 'TOKEN_DATE_INVALID',
        context: {
          rawExpiresAt: expiresAt,
          parsedAsUTC: expiresAtUTC,
          tokenExists: !!token
        }
      });
      console.error('❌ Invalid expiration date:', expiresAt);
      return null;
    }
    
    const isExpired = currentUTCTimestamp >= expirationUTCTimestamp;
    const result = isExpired ? null : token;
    
    // Cache the result
    lastTokenCheck = {
      time: now,
      result: result,
      expiresAt: expiresAt
    };
    
    // Only log expiry warning if actually expired
    if (isExpired) {
      logger.warn('Token has expired', {
        component: 'Record',
        action: 'TOKEN_EXPIRED',
        context: {
          currentUTC: new Date(currentUTCTimestamp).toISOString(),
          expiresUTC: expirationDate.toISOString(),
          timeDiffMinutes: Math.round((expirationUTCTimestamp - currentUTCTimestamp) / (1000 * 60)),
          rawExpiresString: expiresAt
        }
      });
      console.warn('⚠️ Stored token has expired', {
        current_utc: new Date(currentUTCTimestamp).toISOString(),
        expires_utc: expirationDate.toISOString(),
        time_diff_minutes: Math.round((expirationUTCTimestamp - currentUTCTimestamp) / (1000 * 60)),
        raw_expires_string: expiresAt
      });
    } 
    
    return result;
  } catch (error) {
    logger.error('Failed to parse token expiration date', {
      component: 'Record',
      action: 'TOKEN_PARSE_FAILED',
      context: { rawValue: expiresAt },
      error
    });
    console.error('❌ Error parsing token expiration date:', error, 'Raw value:', expiresAt);
    return null;
  }
}

export function normalizeDeepgramMessage(data) {
  if (data.type !== "Results") return null;
 
  const transcript = data.channel?.alternatives?.[0]?.transcript || "";
  if (!transcript.trim()) return null;

  const rawWords = data.channel?.alternatives?.[0]?.words ?? [];
  const words = rawWords.map(w => ({
    word:       w.punctuated_word ?? w.word,
    start:      w.start,
    end:        w.end,
    confidence: w.confidence
  }));

  return {
    text: transcript,
    message_type: data.is_final ? "FinalTranscript" : "PartialTranscript",
    words
  };
}

// Normalize AssemblyAI message format
export function normalizeAssemblyAIMessage(data) {
  if (data.message_type !== "PartialTranscript" && data.message_type !== "FinalTranscript") return null;
  
  const transcript = data.text || "";
  if (!transcript.trim()) return null;
  
  return {
    text: transcript,
    message_type: data.message_type
  };
}

// Unified normalizer that handles both AssemblyAI and Deepgram message formats
export function normalizeTranscriptMessage(data, model) {
  if (model === configDetails.TRANSCRIPTION_MODEL_DEEPGRAM) {
    return normalizeDeepgramMessage(data);
  } else if (model === configDetails.TRANSCRIPTION_MODEL_ASSEMBLYAI) {
    return normalizeAssemblyAIMessage(data);
  } else {
    // Default to AssemblyAI format
    return normalizeAssemblyAIMessage(data);
  }
}